import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { ApiError } from '@/lib/api';
import { roomsApi, type DiscoveredRoom, type RoomFilters } from '@/lib/rooms';
import { connectionsApi, findLiveConnection, type ConnectionView } from '@/lib/connections';
import {
  Button,
  Card,
  CONTROL,
  FilterField,
  LoadingLine,
  Notice,
  PageHeading,
  Refreshing,
  SkeletonRows,
  StatusChip,
  TextButton,
  TogglePill,
} from '@/components/ui';

/**
 * Screen 5 — Discovered rooms.
 *
 * The read-only companion to Space mapping: what we found in the customer's
 * directory, grouped the way their directory groups it, and whether each room
 * is linked to a pod. It also says WHEN discovery last ran, which is the
 * difference between "you have no rooms" and "we have not looked since
 * Tuesday" — two situations needing opposite responses.
 *
 * WHAT THIS TABLE SHOWS, and what it deliberately dropped. Google returns an
 * address that is frequently the room's own name with its capacity repeated
 * after it — "Calendar Adapter-1-calendar adapter test (4)" — so a column for
 * it restated the first column and the capacity column at once, in the widest
 * cell on the row. Where a room IS is its building and floor; that is what the
 * column says now, with the address kept as the cell's tooltip for the
 * directories that do put a real one there.
 */
export function Rooms() {
  const [rooms, setRooms] = useState<DiscoveredRoom[] | null>(null);
  const [filters, setFilters] = useState<RoomFilters | null>(null);
  const [connections, setConnections] = useState<ConnectionView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** A read is in flight. The FIRST one shows a skeleton; later ones dim the rows. */
  const [loading, setLoading] = useState(true);

  const [building, setBuilding] = useState('');
  const [floor, setFloor] = useState('');
  const [search, setSearch] = useState('');
  const [unmappedOnly, setUnmappedOnly] = useState(false);

  /*
   * The search that has actually been sent. Filtering happens on the server —
   * the list runs to 500 rows — so typing "board room" straight through fired
   * nine requests and painted whichever answer came back last.
   */
  const [appliedSearch, setAppliedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setAppliedSearch(search), 250);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [roomPage, filterOptions, connectionList] = await Promise.all([
        roomsApi.list({ building, floor, search: appliedSearch, unmappedOnly, limit: 500 }),
        roomsApi.filters(),
        connectionsApi.list(),
      ]);
      setRooms(roomPage.data);
      setFilters(filterOptions);
      setConnections(connectionList);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'We could not load your rooms.');
    } finally {
      setLoading(false);
    }
  }, [building, floor, appliedSearch, unmappedOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  const connected = findLiveConnection(connections);
  const filtering = Boolean(search || building || floor || unmappedOnly);
  /** Nothing has come back yet, so the page knows nothing to be right about. */
  const firstRead = rooms === null && loading;
  /** Re-reading the customer's directory is the slowest call on this screen. */
  const working = loading || busy;

  async function rediscover() {
    if (!connected?.connection_id) return;
    setBusy(true);
    setNotice(null);
    try {
      const result = await roomsApi.rediscover(connected.connection_id);
      setNotice(
        result.deactivated > 0
          ? `${result.discovered} rooms found. ${result.deactivated} are no longer in your directory and have been marked inactive rather than removed.`
          : `${result.discovered} rooms found${result.added > 0 ? `, ${result.added} new` : ''}.`,
      );
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'We could not re-read your directory.');
    } finally {
      setBusy(false);
    }
  }

  function clearFilters() {
    setSearch('');
    setBuilding('');
    setFloor('');
    setUnmappedOnly(false);
  }

  // Grouped by room list, which is how the provider organises them and
  // therefore how the customer thinks about them.
  const grouped = useMemo(() => {
    const groups = new Map<string, DiscoveredRoom[]>();
    for (const room of rooms ?? []) {
      const key = room.room_list ?? 'Not in a room list';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(room);
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [rooms]);

  const linked = (rooms ?? []).filter((room) => room.mapped_to_space_id).length;

  return (
    <div className="mx-auto w-full max-w-[1100px] px-[28px] py-[40px]">
      <div className="mb-[28px]">
        <PageHeading
          eyebrow="Calendar"
          title="Discovered rooms"
          sub={
            connected?.last_discovery_at
              ? `Last read from your directory ${new Date(connected.last_discovery_at).toLocaleString()}.`
              : 'We have not read your directory yet.'
          }
          actions={
            connected ? (
              <Button tone="quiet" busy={busy} onClick={rediscover}>
                Re-read directory
              </Button>
            ) : undefined
          }
        />
      </div>

      {notice && (
        <div className="mb-[20px]">
          <Notice tone="healthy" title={notice} />
        </div>
      )}
      {error && (
        <div className="mb-[20px]">
          <Notice tone="broken" title={error} />
        </div>
      )}

      {/*
        * Until the first read lands, `connections` is empty and `connected` is
        * undefined — which is indistinguishable from "no calendar connected".
        * Saying so while we are still asking told people to go and connect an
        * account they had already connected, so the skeleton holds the page
        * until there is an answer.
        */}
      {firstRead && <SkeletonRows label="Loading your rooms…" />}

      {!firstRead && !connected && (
        <Notice tone="neutral" title="No calendar is connected yet">
          Connect an account on the Connections screen and we will read your room list.
        </Notice>
      )}

      {!firstRead && connected && (
        <>
          <Card className="mb-[24px]">
            <div className="flex flex-wrap items-end gap-[14px]">
              <FilterField label="Search" grow>
                <input
                  type="search"
                  value={search}
                  placeholder="Room name or address"
                  onChange={(e) => setSearch(e.target.value)}
                  className={CONTROL}
                />
              </FilterField>
              <FilterField label="Building">
                <select
                  value={building}
                  onChange={(e) => setBuilding(e.target.value)}
                  className={CONTROL}
                >
                  <option value="">All</option>
                  {(filters?.buildings ?? []).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </FilterField>
              <FilterField label="Floor">
                <select
                  value={floor}
                  onChange={(e) => setFloor(e.target.value)}
                  className={CONTROL}
                >
                  <option value="">All</option>
                  {(filters?.floors ?? []).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </FilterField>
              <TogglePill checked={unmappedOnly} onChange={setUnmappedOnly}>
                Not linked yet
              </TogglePill>

              <div className="ml-auto flex items-center gap-[14px] text-sub text-muted">
                {/* A filter change re-reads from the server, so the count says
                    so rather than silently disagreeing with the rows. */}
                {working && rooms !== null && (
                  <LoadingLine>{busy ? 'Re-reading your directory…' : 'Updating…'}</LoadingLine>
                )}
                {rooms !== null && !working && (
                  <span>
                    {rooms.length} {rooms.length === 1 ? 'room' : 'rooms'}
                    {linked > 0 && ` · ${linked} linked`}
                  </span>
                )}
                {filtering && !working && (
                  <TextButton onClick={clearFilters}>Clear filters</TextButton>
                )}
              </div>
            </div>
          </Card>

          {rooms === null ? (
            <SkeletonRows label="Loading your rooms…" />
          ) : rooms.length === 0 ? (
            <Notice tone="neutral" title="No rooms match">
              {filtering
                ? 'Try clearing the filters.'
                : 'Your directory has no calendar resources yet. Add your rooms in your admin console, then re-read the directory.'}
            </Notice>
          ) : (
            <Refreshing busy={working}>
              {grouped.map(([roomList, group]) => (
              <section key={roomList} className="mb-[28px]">
                <div className="mb-[10px] flex items-baseline gap-[10px]">
                  <h2 className="m-0 text-lead font-semibold text-ink">{roomList}</h2>
                  <span className="rounded-chip bg-divider px-[8px] py-[2px] text-micro font-semibold text-muted tabular-nums">
                    {group.length}
                  </span>
                </div>

                <Card padded={false} className="overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-body">
                      <thead>
                        <tr className="border-b border-divider bg-page/60 text-left">
                          <Th>Room</Th>
                          <Th>Where</Th>
                          <Th align="right">Capacity</Th>
                          <Th align="right">Linked to</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.map((room) => (
                          <RoomRow key={room.id} room={room} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
                </section>
              ))}
            </Refreshing>
          )}
        </>
      )}
    </div>
  );
}

function RoomRow({ room }: { room: DiscoveredRoom }) {
  // Building and floor is where a room IS. The provider's address field often
  // repeats the name, so it lives in the tooltip rather than in a column.
  const where = [room.building, room.floor ? `floor ${room.floor}` : null]
    .filter(Boolean)
    .join(' · ');

  return (
    <tr className="border-b border-divider transition-colors last:border-0 hover:bg-page">
      <td className="px-[18px] py-[14px] align-top">
        <div className="flex flex-wrap items-center gap-[8px]">
          <span className="font-medium text-ink">{room.name}</span>
          {!room.is_active && <StatusChip tone="attention">No longer in directory</StatusChip>}
        </div>
        {/* The address the room is booked against. Monospace because it is an
            identifier people copy, and truncated because it is long and is
            never what anyone is scanning the column for. */}
        <span
          title={room.calendar_id}
          className="mt-[3px] block max-w-[42ch] truncate font-mono text-caption text-muted"
        >
          {room.calendar_id}
        </span>
      </td>

      <td className="px-[18px] py-[14px] align-top text-muted" title={room.address ?? undefined}>
        {where || room.address || '—'}
      </td>

      <td className="px-[18px] py-[14px] align-top text-right text-muted tabular-nums">
        {room.capacity ?? '—'}
      </td>

      <td className="px-[18px] py-[14px] align-top text-right whitespace-nowrap">
        {room.mapped_to_space_id ? (
          <StatusChip tone="healthy">{room.mapped_to_space_name ?? 'A pod'}</StatusChip>
        ) : (
          /* Linking happens on Space mapping, because a link is a decision
             about a POD — one pod to one room, both ways — and that screen is
             where the pods are. No room in the query: that screen's search box
             filters PODS, so seeding it with a room name would show an empty
             list. */
          <Link
            to="/mappings"
            className="text-sub font-semibold text-brand underline underline-offset-2"
          >
            Link to a pod
          </Link>
        )}
      </td>
    </tr>
  );
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      className={clsx(
        'zs-eyebrow px-[18px] py-[12px] font-semibold text-muted',
        align === 'right' && 'text-right',
      )}
    >
      {children}
    </th>
  );
}
